import { useDrawerEscape } from '../../lib/hooks/useDrawerEscape';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { setBodyScroll, triggerHaptic } from '../../lib/utils/responsive';
import { useTheme } from '../../contexts/ThemeContext';

export interface MobileDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Callback when drawer should close */
  onClose: () => void;
  /** Drawer title */
  title?: string;
  /** Drawer content */
  children: React.ReactNode;
  /** Position of the drawer */
  position?: 'bottom' | 'right' | 'left';
  /** Height for bottom drawer (default: 'auto', max 90vh) */
  height?: 'auto' | 'half' | 'full';
  /** Show handle bar for swipe (bottom drawers only) */
  showHandle?: boolean;
  /** Enable swipe to close */
  swipeToClose?: boolean;
  /** Prevent closing on backdrop click */
  preventBackdropClose?: boolean;
  /** Custom className for drawer content */
  className?: string;
  /** Optional footer content */
  footer?: React.ReactNode;
  /** Remove padding from content area (use when content has its own padding) */
  noPadding?: boolean;
}

/**
 * Mobile-optimized drawer component
 * Slides in from bottom, right, or left
 * Supports swipe-to-close gesture
 * Automatically manages body scroll
 *
 * @example
 * ```tsx
 * <MobileDrawer
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Filters"
 *   showHandle
 *   swipeToClose
 * >
 *   <FilterContent />
 * </MobileDrawer>
 * ```
 */
export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  position = 'bottom',
  height = 'auto',
  showHandle = true,
  swipeToClose = true,
  preventBackdropClose = false,
  className = '',
  footer,
  noPadding = false,
}) => {
  const { isDark } = useTheme();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [drawerRoot, setDrawerRoot] = useState<HTMLElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const touchStartRef = useRef<{ y: number; x: number; time: number } | null>(null);

  // State per gestire l'animazione di mount/unmount
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Gestisce l'animazione di apertura/chiusura
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Piccolo delay per permettere al DOM di montare prima dell'animazione
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // Aspetta che l'animazione di chiusura finisca prima di smontare
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Deve corrispondere alla duration-300
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Create drawer root portal
  useEffect(() => {
    let root = document.getElementById('drawer-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'drawer-root';
      document.body.appendChild(root);
    }
    // Always ensure correct styles (in case element existed with old styles)
    root.style.position = 'fixed';
    root.style.top = '0';
    root.style.left = '0';
    root.style.width = '100%';
    root.style.height = '100%';
    root.style.zIndex = '20000';
    root.style.pointerEvents = 'none';

    setDrawerRoot(root);
  }, []);

  useDrawerEscape(isOpen,onClose,20000);

  // Handle body scroll and escape key
  useEffect(() => {
    if (!isOpen || !drawerRoot) return;

    // Disable body scroll when drawer is open
    setBodyScroll(true);

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      setBodyScroll(false);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose, drawerRoot]);

  // Detect mobile keyboard via visualViewport API
  useEffect(() => {
    if (!isOpen) {
      setKeyboardHeight(0);
      return;
    }

    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      const kbHeight = window.innerHeight - vv.height;
      setKeyboardHeight(kbHeight > 50 ? kbHeight : 0);
    };

    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, [isOpen]);

  // Auto-scroll active input into view when keyboard opens
  useEffect(() => {
    if (keyboardHeight > 0 && drawerRef.current) {
      setTimeout(() => {
        const activeEl = document.activeElement as HTMLElement;
        if (activeEl && drawerRef.current?.contains(activeEl)) {
          activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [keyboardHeight]);

  // Touch gesture handlers for swipe-to-close
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!swipeToClose) return;

    const touch = e.touches[0];
    touchStartRef.current = {
      y: touch.clientY,
      x: touch.clientX,
      time: Date.now(),
    };
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!swipeToClose || !isDragging || !touchStartRef.current) return;

    const touch = e.touches[0];
    const deltaY = touch.clientY - touchStartRef.current.y;
    const deltaX = touch.clientX - touchStartRef.current.x;

    // Only allow dragging in the correct direction
    if (position === 'bottom' && deltaY > 0) {
      setDragOffset(deltaY);
      e.preventDefault();
    } else if (position === 'right' && deltaX > 0) {
      setDragOffset(deltaX);
      e.preventDefault();
    } else if (position === 'left' && deltaX < 0) {
      setDragOffset(Math.abs(deltaX));
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    if (!swipeToClose || !isDragging || !touchStartRef.current) return;

    const threshold = 100; // px
    const duration = Date.now() - touchStartRef.current.time;
    const velocity = dragOffset / duration;

    // Close if dragged past threshold or fast swipe
    if (dragOffset > threshold || velocity > 0.5) {
      triggerHaptic('light');
      onClose();
    }

    // Reset
    setIsDragging(false);
    setDragOffset(0);
    touchStartRef.current = null;
  };

  const handleBackdropClick = () => {
    if (!preventBackdropClose) {
      onClose();
    }
  };

  if (!shouldRender || !drawerRoot) {
    return null;
  }

  // Position-specific styles - usa isAnimating per controllare l'animazione
  const positionClasses = {
    bottom: `
      bottom-0 left-0 right-0
      rounded-t-2xl
      transform transition-transform duration-300 ease-out
      ${isAnimating && !isDragging ? 'translate-y-0' : 'translate-y-full'}
    `,
    right: `
      top-0 right-0 bottom-0
      w-80 max-w-full
      rounded-l-2xl
      transform transition-transform duration-300 ease-out
      ${isAnimating && !isDragging ? 'translate-x-0' : 'translate-x-full'}
    `,
    left: `
      top-0 left-0 bottom-0
      w-80 max-w-full
      rounded-r-2xl
      transform transition-transform duration-300 ease-out
      ${isAnimating && !isDragging ? 'translate-x-0' : '-translate-x-full'}
    `,
  };

  const heightClasses = position === 'bottom' ? {
    auto: 'max-h-[90vh]',
    half: 'h-[50vh]',
    full: 'h-[95vh]',
  } : {};

  // Drag transform
  const dragTransform = isDragging ? {
    bottom: `translateY(${dragOffset}px)`,
    right: `translateX(${dragOffset}px)`,
    left: `translateX(-${dragOffset}px)`,
  }[position] : undefined;

  const drawerContent = (
    <>
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0 bg-black/50
          transition-opacity duration-300
          ${isAnimating ? 'opacity-100' : 'opacity-0'}
        `}
        style={{ pointerEvents: shouldRender ? 'auto' : 'none' }}
        onClick={() => isOpen && handleBackdropClick()}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`
          fixed
          glass-card
          shadow-2xl
          flex flex-col
          ${positionClasses[position]}
          ${position === 'bottom' ? heightClasses[height] : ''}
          ${className}
        `}
        style={{
          pointerEvents: 'auto',
          transform: dragTransform,
          transition: isDragging ? 'none' : undefined,
          background: isDark
            ? 'rgb(17, 24, 39)'
            : 'rgb(255, 255, 255)',
          boxShadow: isDark
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          // Adjust height when mobile keyboard is open
          ...(position === 'bottom' && keyboardHeight > 0 ? {
            height: height === 'full' ? `calc(95vh - ${keyboardHeight}px)` : undefined,
            maxHeight: height === 'auto' ? `calc(90vh - ${keyboardHeight}px)` : height === 'half' ? `calc(50vh - ${keyboardHeight}px)` : undefined,
          } : {}),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar (bottom drawers only) - swipe zone */}
        {position === 'bottom' && showHandle && (
          <div
            className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="w-12 h-1 bg-gray-400 dark:bg-gray-600 rounded-full" />
          </div>
        )}

        {/* Header - also swipe zone */}
        {title && (
          <div
            className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <button
              onClick={() => {
                triggerHaptic('light');
                onClose();
              }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors touch-action-manipulation text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              style={{ minWidth: '44px', minHeight: '44px' }}
              aria-label="Close drawer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className={noPadding ? '' : 'p-4'}>
            {children}
          </div>
        </div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex-shrink-0 bg-gray-50 dark:bg-gray-800/50">
            {footer}
          </div>
        )}
      </div>
    </>
  );

  return createPortal(drawerContent, drawerRoot);
};

/**
 * Hook to manage drawer state
 *
 * @example
 * ```tsx
 * const drawer = useDrawer();
 *
 * return (
 *   <>
 *     <button onClick={drawer.open}>Open Filters</button>
 *     <MobileDrawer {...drawer} title="Filters">
 *       <FilterContent />
 *     </MobileDrawer>
 *   </>
 * );
 * ```
 */
export const useDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
    onClose: () => setIsOpen(false),
  };
};
